import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, asError } from "@/api/client";

const AuthContext = createContext(null);

const TOKEN_KEY = "nt_token";
const USER_KEY = "nt_auth_user_v2";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
  });
  const [bootstrapping, setBootstrapping] = useState(true);

  // Verify session on mount if token exists — refresh user data (covers role changes / disabled).
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setBootstrapping(false); return; }
    api.get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        localStorage.setItem(USER_KEY, JSON.stringify(data));
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
      })
      .finally(() => setBootstrapping(false));
  }, []);

  const persist = (u, t) => {
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else if (t === null) localStorage.removeItem(TOKEN_KEY);
    setUser(u);
  };

  const signup = useCallback(async ({ name, email, password }) => {
    try {
      const { data } = await api.post("/auth/signup", { name, email, password });
      persist(data.user, data.token);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: asError(e) };
    }
  }, []);

  const login = useCallback(async ({ email, password }) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      persist(data.user, data.token);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: asError(e) };
    }
  }, []);

  const logout = useCallback(() => {
    persist(null, null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, signup, login, logout, bootstrapping, isAuthed: !!user, isAdmin: user?.role === "admin" }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
